import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { projects } from "@/lib/portfolioData";
import { projectDetails } from "@/lib/projectDetails";
import ProjectShop from "./ProjectShop";

/* ---------- Reusable garment tile ---------- */
function GarmentTile({ g, accent }) {
  const dotColor = accent === "lime" ? "bg-neon-lime" : "bg-neon-pink";

  return (
    <figure data-testid={`garment-tile-${g.label.toLowerCase().replace(/\s+/g, "-")}`} className="group">
      <div className="relative aspect-[3/4] bg-[#ece8de] ring-1 ring-black/10 overflow-hidden">
        {g.image ? (
          <img
            src={g.image}
            alt={g.label}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <PlaceholderArt palette={g.palette} />
        )}
        {/* Tag */}
        <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/80 bg-black/60 backdrop-blur-sm px-2 py-1">
          № {g.label}
        </span>
      </div>
      <figcaption className="mt-4 flex items-center justify-between">
        <span className="font-display text-2xl md:text-3xl tracking-tight">
          {g.label.toLowerCase()}
        </span>
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-black/45">
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
          {g.note}
        </span>
      </figcaption>
    </figure>
  );
}

/* Decorative placeholder so the page looks intentional until the user adds garment photos */
function PlaceholderArt({ palette = ["#FF007F", "#39FF14", "#000"] }) {
  const [a, b, c] = palette;
  return (
    <div className="absolute inset-0">
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${a} 0%, ${b} 60%, ${c} 100%)`,
          opacity: 0.65,
        }}
      />
      <div
        className="absolute inset-0 mix-blend-overlay opacity-50"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 2px, transparent 2px 14px)",
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80 bg-black/40 px-3 py-1">
          Garment photo · soon
        </span>
      </div>
    </div>
  );
}

/* ---------- Video section ---------- */
function VideoSection({ data, accentText }) {
  if (!data) return null;

  const toEmbed = (url) => {
    if (!url) return null;
    // YouTube
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`;
    // Vimeo
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
    return null;
  };

  const embedUrl = data.url ? toEmbed(data.url) : null;
  const directUrl = data.url && !embedUrl ? data.url : null;

  return (
    <section
      data-testid="project-video"
      className="relative px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-black/10"
    >
      <header className="flex items-end justify-between gap-6 mb-12 md:mb-16 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/55">
            {data.eyebrow || "Film · Moving image"}
          </p>
          <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {data.title || (
              <>
                In <span className={`italic ${accentText}`}>motion</span>.
              </>
            )}
          </h2>
        </div>
        {data.note && (
          <p className="max-w-md text-black/60 text-sm md:text-base leading-relaxed">
            {data.note}
          </p>
        )}
      </header>

      <div className="relative w-full aspect-video bg-[#ece8de] ring-1 ring-black/10 overflow-hidden">
        {embedUrl && (
          <iframe
            src={embedUrl}
            title={data.title || "Project video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        )}
        {directUrl && (
          <video
            src={directUrl}
            poster={data.poster || undefined}
            controls
            playsInline
            className="absolute inset-0 w-full h-full object-cover bg-black"
          />
        )}
        {!embedUrl && !directUrl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 bg-[#ece8de]">
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-black/45">
              Video slot
            </span>
            <span className="mt-6 font-display italic text-3xl md:text-4xl text-black/70 max-w-[20ch]">
              {data.placeholder || "Film coming soon"}
            </span>
            <span className="mt-6 text-[10px] font-mono uppercase tracking-[0.24em] text-black/35">
              Upload MP4 or paste a YouTube / Vimeo link to publish
            </span>
          </div>
        )}
      </div>

      {data.caption && (
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.28em] text-black/45">
          {data.caption}
        </p>
      )}
    </section>
  );
}

/* ---------- Process / Sketchbook — horizontal carousel ---------- */
function ProcessSection({ data }) {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const total = data?.images?.length || 0;

  /* Update active page + nav button states based on scroll position */
  const updateState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const slides = el.querySelectorAll("[data-slide]");
    if (!slides.length) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    slides.forEach((s, i) => {
      const mid = s.offsetLeft + s.offsetWidth / 2;
      const d = Math.abs(mid - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setActiveIdx(best);
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateState();
    el.addEventListener("scroll", updateState, { passive: true });
    window.addEventListener("resize", updateState);
    return () => {
      el.removeEventListener("scroll", updateState);
      window.removeEventListener("resize", updateState);
    };
  }, [updateState, total]);

  /* Scroll to a specific slide index */
  const scrollToIndex = useCallback((idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slides = el.querySelectorAll("[data-slide]");
    const target = slides[Math.max(0, Math.min(idx, slides.length - 1))];
    if (!target) return;
    const left = target.offsetLeft - (el.clientWidth - target.offsetWidth) / 2;
    el.scrollTo({ left, behavior: "smooth" });
  }, []);

  const prev = () => scrollToIndex(activeIdx - 1);
  const next = () => scrollToIndex(activeIdx + 1);

  /* Convert vertical wheel to horizontal scroll while hovering the carousel */
  const onWheel = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      el.scrollBy({ left: e.deltaY, behavior: "auto" });
    }
  };

  /* Pointer drag (desktop mouse "grab and drag") */
  const dragState = useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });
  const onPointerDown = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (e.pointerType === "mouse") {
      dragState.current = {
        active: true,
        startX: e.clientX,
        startScroll: el.scrollLeft,
        moved: 0,
      };
      el.setPointerCapture(e.pointerId);
      el.classList.add("cursor-grabbing");
    }
  };
  const onPointerMove = (e) => {
    const el = scrollerRef.current;
    if (!el || !dragState.current.active) return;
    const dx = e.clientX - dragState.current.startX;
    dragState.current.moved = Math.abs(dx);
    el.scrollLeft = dragState.current.startScroll - dx;
  };
  const endDrag = (e) => {
    const el = scrollerRef.current;
    if (!el || !dragState.current.active) return;
    dragState.current.active = false;
    el.releasePointerCapture?.(e.pointerId);
    el.classList.remove("cursor-grabbing");
    // Snap to the closest slide after drag
    setTimeout(() => {
      const slides = el.querySelectorAll("[data-slide]");
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((s, i) => {
        const mid = s.offsetLeft + s.offsetWidth / 2;
        const d = Math.abs(mid - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      scrollToIndex(best);
    }, 0);
  };

  /* Keyboard navigation */
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  if (!data || !total) return null;

  const accentText =
    data.heading?.accentColor === "lime" ? "text-neon-lime" : "text-neon-pink";

  return (
    <section
      data-testid="project-process"
      className="relative py-24 md:py-32 border-t border-black/10 overflow-hidden"
    >
      {/* Subtle paper-grid texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Header */}
      <header className="relative z-10 px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-12 md:mb-16 items-end">
        <div className="lg:col-span-7">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/55">
            {data.eyebrow || "Process · Inspiration"}
          </p>
          <h2
            data-testid="process-heading"
            className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
          >
            <span className={`italic ${accentText}`}>Sketchbook</span>
          </h2>
        </div>
      </header>

      {/* Toolbar */}
      <div className="relative z-10 px-6 md:px-10 lg:px-16 flex items-center justify-between mb-6 md:mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/55 flex items-center gap-3">
          <span className="text-black/85" data-testid="process-counter">
            Plate {String(activeIdx + 1).padStart(2, "0")}
            <span className="text-black/35">
              {" "}
              / {String(total).padStart(2, "0")}
            </span>
          </span>
          <span className="hidden md:inline text-black/30">
            · drag · swipe · arrow keys · scroll
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={prev}
            disabled={!canPrev}
            data-testid="process-prev"
            aria-label="Previous plate"
            className={`w-11 h-11 md:w-12 md:h-12 rounded-full border flex items-center justify-center transition-all
              ${canPrev
                ? "border-black/40 text-black hover:border-neon-pink hover:text-neon-pink"
                : "border-black/10 text-black/25 cursor-not-allowed"
              }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M19 12H5M11 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!canNext}
            data-testid="process-next"
            aria-label="Next plate"
            className={`w-11 h-11 md:w-12 md:h-12 rounded-full border flex items-center justify-center transition-all
              ${canNext
                ? "border-black/40 text-black hover:border-neon-pink hover:text-neon-pink"
                : "border-black/10 text-black/25 cursor-not-allowed"
              }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Carousel */}
      <div
        ref={scrollerRef}
        data-testid="process-carousel"
        tabIndex={0}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        className="relative z-10 flex items-stretch gap-6 md:gap-10 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pb-6 cursor-grab select-none focus:outline-none scroll-smooth"
        style={{
          scrollbarWidth: "none",
          paddingLeft: "max(1.5rem, calc((100vw - 1100px) / 2))",
          paddingRight: "max(1.5rem, calc((100vw - 1100px) / 2))",
        }}
      >
        <style>{`[data-testid="process-carousel"]::-webkit-scrollbar{display:none}`}</style>

        {data.images.map((img, i) => (
          <figure
            key={i}
            data-slide
            data-testid={`process-image-${i}`}
            className="relative shrink-0 snap-center bg-[#f6f3ed] ring-1 ring-black/10 overflow-hidden flex flex-col"
            style={{
              width: "min(86vw, 1000px)",
              maxHeight: "82vh",
            }}
          >
            <div className="relative flex-1 overflow-hidden bg-[#f6f3ed] min-h-[420px] md:min-h-[540px]">
              {img.src ? (
                <img
                  src={img.src}
                  alt={img.caption || `Process page ${i + 1}`}
                  loading={i < 2 ? "eager" : "lazy"}
                  draggable={false}
                  className="w-full h-full object-contain block pointer-events-none"
                  style={{ maxHeight: "calc(82vh - 56px)" }}
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-black/40">
                    Plate {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="mt-6 font-display italic text-4xl md:text-5xl text-black/70 leading-tight max-w-[16ch]">
                    {img.caption || "Process page soon"}
                  </span>
                  <span className="mt-8 text-[10px] font-mono uppercase tracking-[0.24em] text-black/40">
                    Image coming soon
                  </span>
                </div>
              )}
            </div>
          </figure>
        ))}
      </div>

      {/* Dot indicator */}
      <div className="relative z-10 mt-6 md:mt-8 flex items-center justify-center gap-2">
        {data.images.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollToIndex(i)}
            aria-label={`Go to plate ${i + 1}`}
            data-testid={`process-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === activeIdx
                ? "w-8 bg-neon-pink"
                : "w-1.5 bg-black/25 hover:bg-black/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

/* ---------- More projects (portfolio tab) ---------- */
function MoreProjects({ currentSlug }) {
  const others = projects.filter((p) => p.slug !== currentSlug);
  if (!others.length) return null;

  return (
    <section
      data-testid="more-projects"
      className="relative px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-black/10 overflow-hidden"
    >
      {/* Subtle dot grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />

      <header className="relative z-10 flex items-end justify-between gap-6 mb-12 md:mb-16 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/55">
            Keep listening — Portfolio
          </p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            More from the <span className="italic text-neon-pink">studio</span>.
          </h2>
        </div>
        <Link
          to="/#work"
          data-testid="more-projects-all"
          className="font-mono text-xs uppercase tracking-[0.22em] link-underline text-neon-lime"
        >
          All tracks →
        </Link>
      </header>

      <ul className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {others.map((p) => {
          const has = !!projectDetails[p.slug];
          const accentText =
            p.accent === "lime" ? "text-neon-lime" : "text-neon-pink";
          const accentBg = p.accent === "lime" ? "bg-neon-lime" : "bg-neon-pink";

          const inner = (
            <div className="group relative">
              <div className="relative aspect-square rounded-full overflow-hidden ring-1 ring-black/10 group-hover:ring-black/40 transition-all duration-500">
                <div className="absolute inset-0 animate-spin-slow group-hover:[animation-duration:5s]">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute inset-[8%] rounded-full border border-white/10" />
                  <span className="absolute inset-[24%] rounded-full border border-white/15" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="relative w-[26%] h-[26%] rounded-full bg-black ring-1 ring-white/30 flex items-center justify-center">
                    <div className={`w-[35%] h-[35%] rounded-full ${accentBg}`} />
                  </div>
                </div>
                {!has && (
                  <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.22em] text-white/70 bg-black/60 backdrop-blur-sm px-2 py-1">
                    Soon
                  </span>
                )}
              </div>

              <div className="mt-4">
                <p
                  className={`font-mono text-[10px] uppercase tracking-[0.22em] ${accentText}`}
                >
                  № {p.id} · {p.year}
                </p>
                <h3 className="mt-1 font-display text-xl md:text-2xl tracking-tight leading-[1.05]">
                  {p.title}
                </h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-black/40">
                  {p.role.split(" · ")[0]}
                </p>
              </div>
            </div>
          );

          return (
            <li key={p.id} data-testid={`more-project-${p.id}`}>
              {has ? (
                <Link to={`/projects/${p.slug}`} className="block">
                  {inner}
                </Link>
              ) : (
                <div className="block" aria-label={`${p.title} — coming soon`}>
                  {inner}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ---------- Page ---------- */
export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const project = projects.find((p) => p.slug === slug);
  const detail = projectDetails[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!project) {
    return (
      <section className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-black/60">
          404 — Not Found
        </p>
        <h1 className="font-display text-5xl md:text-6xl">No such track.</h1>
        <Link
          to="/"
          className="mt-4 font-mono text-xs uppercase tracking-[0.22em] link-underline text-neon-pink"
          data-testid="back-home-404"
        >
          ← Back to portfolio
        </Link>
      </section>
    );
  }

  /* Fallback for projects that don't yet have full details */
  const data = detail || {
    hero: { image: project.image, caption: `${project.title} · ${project.year}` },
    secondary: null,
    description: [project.blurb],
    credit: "Mia Cho",
    garments: [],
  };

  const accent = project.accent || "pink";
  const accentText = accent === "lime" ? "text-neon-lime" : "text-neon-pink";
  const accentBorder = accent === "lime" ? "border-neon-lime" : "border-neon-pink";

  return (
    <article
      data-testid="project-detail"
      className="min-h-screen bg-[#f6f3ed] text-black grain"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-30 backdrop-blur-md bg-[#f6f3ed]/85 border-b border-black/10">
        <div className="flex items-center justify-between px-6 md:px-10 py-4">
          <button
            type="button"
            onClick={() => navigate("/#work")}
            data-testid="project-back"
            className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-black/80 hover:text-neon-pink transition-colors"
          >
            <span className="w-9 h-9 rounded-full border border-black/30 group-hover:border-neon-pink flex items-center justify-center transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M19 12H5M11 18l-6-6 6-6" />
              </svg>
            </span>
            <span>Back to work</span>
          </button>

          <Link
            to="/"
            data-testid="project-logo"
            className="font-display italic text-xl link-underline"
          >
            Mia<span className="text-neon-pink">·</span>Cho
          </Link>

          <div className="hidden md:flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-black/50">
            <span className={`w-2 h-2 rounded-full ${accent === "lime" ? "bg-neon-lime" : "bg-neon-pink"}`} />
            <span>№ {project.id} · {project.year}</span>
          </div>
        </div>
      </div>

      {/* FEATURE IMAGE — optional additional photo + label between hero and body */}
      {data.feature && (
        <section
          data-testid="project-feature"
          className="px-6 md:px-10 lg:px-16 pt-16 md:pt-24"
        >
          <div className="relative w-full">
            <img
              src={data.feature.image}
              alt={data.feature.label || "Feature image"}
              loading="lazy"
              className="w-full max-h-[80vh] object-cover ring-1 ring-black/10"
            />
            {data.feature.label && (
              <p className="mt-5 md:mt-6 font-display italic text-3xl md:text-4xl lg:text-5xl leading-[1] tracking-tight">
                <span className={`${accentText}`}>·</span> {data.feature.label}
              </p>
            )}
            {data.feature.caption && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.28em] text-black/45">
                {data.feature.caption}
              </p>
            )}
          </div>
        </section>
      )}

      {/* TITLE + DESCRIPTION + SECONDARY IMAGE */}
      <section
        data-testid="project-detail-body"
        className="px-6 md:px-10 lg:px-16 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
      >
        {/* Left — secondary photo */}
        <div className="lg:col-span-6">
          {data.secondary ? (
            <figure className="relative">
              <img
                src={data.secondary.image}
                alt={data.secondary.caption || project.title}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover ring-1 ring-black/10"
              />
              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-black/45">
                {data.secondary.caption}
              </figcaption>
            </figure>
          ) : (
            <div className="aspect-[4/5] bg-[#ece8de] ring-1 ring-black/10 flex items-center justify-center">
              <PlaceholderArt palette={["#FF007F", "#39FF14", "#000"]} />
            </div>
          )}
        </div>

        {/* Right — title + description */}
        <div className="lg:col-span-6 lg:pl-6">
          {slug !== "biba-queen" && (
            <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${accentText}`}>
              ★ Project File · № {project.id}
            </p>
          )}

          <h1
            data-testid="project-title"
            className="mt-5 font-display leading-[0.9] tracking-tight text-5xl md:text-6xl lg:text-7xl"
          >
            Project:{" "}
            <span className={`italic ${accentText}`}>
              {project.title.toLowerCase()}
            </span>
          </h1>

          {slug !== "biba-queen" && (
            <>
              <div className="mt-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-black/50">
                <span>{project.year}</span>
                <span className="w-1 h-1 rounded-full bg-black/30" />
                <span>{project.role}</span>
              </div>

              <div className="mt-10 space-y-6 max-w-[58ch]">
                {data.description.map((p, i) => (
                  <p
                    key={i}
                    className={`text-base md:text-lg leading-relaxed ${i === 0 ? "text-black/90" : "text-black/70"}`}
                    data-testid={`project-paragraph-${i}`}
                  >
                    {p}
                  </p>
                ))}
              </div>

              <p className="mt-12 text-right font-display italic text-black/60">
                — {data.credit}
              </p>
            </>
          )}
        </div>
      </section>

      {/* SHOP — Net-a-Porter style capsule */}
      <ProjectShop
        products={data.shop || []}
        title={`Shop the ${project.title}`}
        subtitle="A limited atelier capsule — made-to-order in Mia Cho's New York studio. Hover (or tap on mobile) any piece to see it styled."
      />

      {/* VIDEO */}
      <VideoSection data={data.video} accentText={accentText} />

      {/* PROCESS / SKETCHBOOK */}
      <ProcessSection data={data.process} />

      {/* MORE FROM THE PORTFOLIO */}
      <MoreProjects currentSlug={project.slug} />

      {/* GARMENT ROW */}
      {data.garments && data.garments.length > 0 && (
        <section
          data-testid="garments-row"
          className="px-6 md:px-10 lg:px-16 pb-28 md:pb-40 border-t border-black/10 pt-20 md:pt-28"
        >
          <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-black/55">
                The Pieces
              </p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight">
                Garment <span className={`italic ${accentText}`}>file</span>.
              </h2>
            </div>
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.22em] text-black/40">
              {data.garments.length} pieces · studio shot
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {data.garments.map((g, i) => (
              <GarmentTile key={i} g={g} accent={accent} />
            ))}
          </div>
        </section>
      )}

      {/* FOOTER NAV */}
      <section className="px-6 md:px-10 lg:px-16 pb-20 border-t border-black/10 pt-14">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <button
            type="button"
            onClick={() => navigate("/#work")}
            data-testid="project-back-bottom"
            className={`group inline-flex items-center gap-3 px-7 py-4 border ${accentBorder} ${accentText} font-mono text-xs uppercase tracking-[0.22em] hover:bg-current transition-colors`}
          >
            <span className="group-hover:text-black transition-colors">← Back to all work</span>
          </button>

          <p className="font-display italic text-black/50">
            Thanks for visiting · {project.title}
          </p>
        </div>
      </section>
    </article>
  );
}
